import * as React from 'react';

import config from 'js/app/config';

import SettingsHelp from 'bundles/author-common/components/SettingsHelp';
import AuthoringPartnerAPIUtils from 'bundles/author-partner/utils/AuthoringPartnerAPIUtils';
import SlugEditor, { INVALID_SLUG_REGEX, MAX_SLUG_LENGTH } from 'bundles/teach-course/components/SlugEditor';
import type { SlugValidationState } from 'bundles/teach-course/constants/SlugValidationState';
import {
  BadFormat,
  InProgress,
  SlugTooLong,
  Unavailable,
  Unknown,
  Valid,
} from 'bundles/teach-course/constants/SlugValidationState';

import _t from 'i18n!nls/author-partner';

const INSTITUTION_PREFIX = `${config.url.base}partners/`;

type Props = {
  shortName: string;
  onChange: (value: string, valid: boolean) => void;
  existingName?: string;
  disabled?: boolean;
};

type State = {
  validationState: SlugValidationState;
};

class PartnerShortNameEditor extends React.Component<Props, State> {
  state = {
    validationState: new Unknown(),
  };

  handleValidation = (valueToValidate: string) => {
    const { existingName, onChange } = this.props;

    // if the name has not changed or was changed back to the original value (say we're in the edit form)
    // then let validation pass and skip the API call to validate (below)
    if (existingName && valueToValidate === existingName) {
      this.setState({ validationState: new Valid() });
      onChange(valueToValidate, true);
      return;
    }

    // to validate, we can just call the authoringPartner.v1 endpoint with the proposed short name
    // if anything returns then we know it's taken
    if (!valueToValidate || INVALID_SLUG_REGEX.test(valueToValidate)) {
      // Do not try to validate a blank, or poorly-formatted one
      this.setState({ validationState: new BadFormat() });
      onChange(valueToValidate, false);
    } else if (valueToValidate.length > MAX_SLUG_LENGTH) {
      // Do not try to validate a blank, or poorly-formatted one
      this.setState({ validationState: new SlugTooLong({ maxLength: MAX_SLUG_LENGTH }) });
      onChange(valueToValidate, false);
    } else {
      this.setState({ validationState: new InProgress() });
      AuthoringPartnerAPIUtils.getAuthoringPartnerByShortName(valueToValidate).then((response) => {
        if (response.elements.length === 0) {
          this.setState({ validationState: new Valid() });
          onChange(valueToValidate, true);
        } else {
          this.setState({ validationState: new Unavailable() });
          onChange(valueToValidate, false);
        }
      });
    }
  };

  render() {
    const { shortName, onChange, disabled } = this.props;
    const { validationState } = this.state;

    return (
      <div className="rc-PartnerShortNameEditor">
        <SettingsHelp>
          {_t(`
            This short name will be used to create the URL for your institution’s landing page, such as www.coursera.org/partners/princeton,
            or www.coursera.org/partners/penn. It should be all lowercase.
          `)}
        </SettingsHelp>
        <SlugEditor
          onChange={onChange}
          prefix={INSTITUTION_PREFIX}
          value={shortName}
          validationState={validationState}
          onValidate={this.handleValidation}
          disabled={disabled || false}
        />
      </div>
    );
  }
}

export default PartnerShortNameEditor;
